// ink-component
import React, { useContext } from 'react';

import useDeprecatedWarning from '../../../hooks/useDeprecatedWarning';
import getGlobalProps from '../../../helpers/constants/global-props';

import { bemHelper, isInputComponent } from '../../../helpers';
import { hasActions, hasCheckbox, hasControl, hasOperators } from '../helpers/helpers';

import TableContext from '../TableContext/TableContext';
import useOffsetLeft from '../hooks/useOffsetLeft';

export type Ribbon = {
  text: string;
  color: 'green' | 'red' | 'blue' | 'orange' | 'yellow' | 'gray' | 'purple' | 'pink';
};

export interface TableCellProps {
  /** Contents */
  children?: any;
  /** Modifier for key cells used in keyPairTables */
  isKey?: boolean;
  /** Modifier for shaded */
  shaded?: boolean;
  /** Modifier for right align */
  right?: boolean;
  /** Modifier for bold */
  bold?: boolean;
  /** Modifier for actions cells. This prop can be omitted if
   * you are passing an Ink Button or Dropdown Component to the last Cell in a table row */
  actions?: boolean;
  /** Modifier for checkbox cells. This prop can be omitted if
   * you are passing an Ink Checkbox Component to the Cell */
  checkbox?: boolean;
  /** Modifier for operator cells. This prop can be omitted if
   * you are passing an Ink Button Component to the Cell with the operator prop */
  operators?: boolean;
  /** Modifier for control cells. This prop can be omitted if
   * you are passing an Ink Input Component to the Cell with the isCell prop */
  control?: boolean;
  /** Freezes the cell to the left when the table is scrolled. You should **always**
   * pass this prop to the **header and all the cells in the same column**.
   * If you want to freeze **more than one column** in a table, make sure you freeze them
   * **in a sequence**. From the first column to the last one you want to freeze,
   * without skipping any cell or header in between. */
  freeze?: boolean;
  /** HTML colspan attribute */
  colSpan?: number;
  /** Highlights the specific cell with the chosen color */
  highlight?: 'blue' | 'green' | 'red' | 'yellow' | 'white';
  /** Defines if the cell has a left blue border */
  dividedLeft?: boolean;

  /** @ignore */
  totals?: boolean;
  /** @ignore */
  onClick?: () => void;
  /** @ignore */
  twiddleContent?: boolean;
  /** @ignore */
  twiddleTrigger?: boolean;
  /** @ignore */
  twiddle?: boolean;
  /** @ignore */
  ribbon?: string | Ribbon;
  /** @ignore */
  stripe?: string;
  /** @ignore */
  last?: boolean;
  /** @ignore */
  indent?: 1 | 2 | 3 | 4;
}

const globalProps = getGlobalProps({ deprecated: true, inkSass: true });

/**
 * @categories table
 * @purpose Splits table body rows into columns
 * @deprecated This component is deprecated and replaced by [NewTable.Cell](https://ink.carta.com/components/NewTableCell/)
 * @status deprecated
 * @replacementComponent NewTable.Cell
 * @dateOfDeletion December 1st, 2023
 */
function TableCell(props: TableCellProps) {
  useDeprecatedWarning({ componentName: 'TableCell', substitute: 'NewTable.Cell' });

  const { mini, density } = useContext(TableContext);
  const [offsetLeft, cellTagRef] = useOffsetLeft(props.freeze);

  function defineRibbonClassName(): string | undefined {
    const { ribbon } = props;
    const b = bemHelper();

    if (typeof ribbon === 'string') {
      return b('ink-ribbon', {
        [ribbon]: Boolean(ribbon),
      });
    }
    if (typeof ribbon === 'object') {
      return b('ink-ribbon', {
        [ribbon && ribbon.color]: Boolean(ribbon),
      });
    }
    return undefined;
  }

  function defineStripeClassName(): string | undefined {
    const { stripe } = props;
    const b = bemHelper();

    if (typeof stripe === 'string') {
      return b('ink-stripe', {
        [stripe]: Boolean(stripe),
      });
    }
    return undefined;
  }

  function defineHighlightClassName() {
    return props.highlight ? ` ink-highlight ink-highlight__${props.highlight}` : '';
  }

  function defineClassName() {
    const {
      shaded,
      twiddle,
      twiddleContent,
      bold,
      totals,
      control,
      right,
      isKey,
      last,
      freeze,
      indent,
      actions,
      checkbox,
      operators,
      dividedLeft,
      twiddleTrigger,
    } = props;
    const b = bemHelper();

    return b('ink-table--cell', {
      shaded: shaded || twiddle || twiddleContent,
      twiddle: twiddleTrigger,
      bold,
      totals,
      key: isKey,
      right,
      actions: actions || (last && hasActions(props)),
      checkbox: checkbox || hasCheckbox(props),
      operators: operators || hasOperators(props),
      control: control || hasControl(props),
      freeze,
      mini,
      [`indent-${indent}`]: Boolean(indent),
      [`density-${density}`]: Boolean(density),
      'divided-left': dividedLeft,
    });
  }

  function handleClick(): void {
    const { onClick, control, actions, children } = props;
    if (onClick && !control && !actions && !isInputComponent(children) && !hasActions(props)) {
      onClick();
    }
  }

  const CellTag = props.isKey ? 'th' : 'td';

  return (
    <>
      {props.stripe !== 'shaded' && (
        <CellTag
          onClick={handleClick}
          className={`${defineClassName()}${defineHighlightClassName()}`}
          colSpan={props.colSpan}
          data-testid={props['data-testid']}
          ref={cellTagRef}
          style={props.freeze ? { left: offsetLeft } : undefined}
          {...globalProps}
        >
          {props.ribbon && (
            <div
              className={defineRibbonClassName()}
              data-ribbon={props.ribbon && typeof props.ribbon === 'object' && props.ribbon.text}
            />
          )}
          {props.stripe && <div className={defineStripeClassName()} />}
          {props.children}
        </CellTag>
      )}
    </>
  );
}

TableCell.defaultProps = {
  children: undefined,
  isKey: false,
  shaded: false,
  right: false,
  bold: false,
  actions: false,
  checkbox: false,
  operators: false,
  control: false,
  freeze: false,
  colSpan: 1,
  highlight: undefined,
  dividedLeft: false,
};

TableCell.displayName = 'TableCell';

export default TableCell;
